// src/api.js
import { fetchAuthSession, signOut } from 'aws-amplify/auth';

const BASE = 'https://3z7hi3z1la.execute-api.ap-southeast-2.amazonaws.com/prod';

// Fetch a valid Cognito idToken dynamically before each request.
// Amplify v6 auto-refreshes the session if the token is expired.
async function getValidToken() {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
}

// Hard-boot a user whose session is genuinely dead (refresh token expired,
// user disabled in Cognito, etc.). Signs out to clear Amplify's stored session,
// then hard-redirects to '/' — reloading the app from scratch, wiping in-memory
// React state, and landing the user at the sign-in gate. Only ever called AFTER
// a force-refresh retry has already failed, so a transient 401 that the retry
// fixes will NOT evict the user mid-work.
async function handleDeadSession() {
  try {
    await signOut();
  } catch (e) {
    console.error('Error signing out:', e);
  }
  window.location.assign('/');
}

// We still accept the 'staleToken' parameter so we don't break the React
// components that call this, but we completely IGNORE it (token is fetched
// fresh via getValidToken on every request).
async function authFetch(path, options = {}, staleToken) {
  const buildInit = (tok) => ({
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tok}`,
      ...(options.headers || {}),
    },
  });

  let freshToken;
  try {
    freshToken = await getValidToken();
  } catch (e) {
    // Can't even obtain a token — refresh token is dead before we send.
    await handleDeadSession();
    return Promise.reject(new Error('Session expired'));
  }

  let res = await fetch(`${BASE}${path}`, buildInit(freshToken));

  // Safety net: getValidToken() already auto-refreshes an expired token before
  // the request, so a 401 here is rare (clock skew, just-expired in-flight,
  // server-side rejection). Force a refresh and retry ONCE.
  if (res.status === 401) {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      freshToken = session.tokens?.idToken?.toString();
      res = await fetch(`${BASE}${path}`, buildInit(freshToken));
    } catch (e) {
      // Refresh itself failed — session is genuinely dead.
      await handleDeadSession();
      return Promise.reject(new Error('Session expired'));
    }
  }

  if (!res.ok) {
    // A 401 that survives the retry means the session is dead — evict cleanly
    // rather than stranding the user on a dashboard where every call fails.
    if (res.status === 401) {
      await handleDeadSession();
      return Promise.reject(new Error('Session expired'));
    }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ── User / onboarding ─────────────────────────────────────────────────────────

export async function getMe(token) {
  return authFetch('/users/me', {}, token);
}

export async function acceptTcs(token, practitionerType) {
  return authFetch('/users/accept-tcs', {
    method: 'POST',
    body: JSON.stringify({
      tcs_accepted:           true,
      practitioner_confirmed: true,
      practitioner_type:      practitionerType,
    }),
  }, token);
}

// ── Matters ───────────────────────────────────────────────────────────────────

export async function listMatters(token) {
  return authFetch('/matters', {}, token);
}

export async function createMatter(token, matterName, retentionDays = 30) {
  return authFetch('/matters', {
    method: 'POST',
    body: JSON.stringify({ matter_name: matterName, retention_days: retentionDays }),
  }, token);
}

// Extend retention ("Keep active" button). Served by the LEGACY /unarchive route
// (Archive was removed 24 Jun 2026; the route now resets last_activity_at to push
// out the deletion horizon). URL is legacy; behaviour is "extend retention".
export async function extendMatter(token, matterId) {
  return authFetch(`/matters/${matterId}/unarchive`, { method: 'PATCH' }, token);
}

export async function deleteMatter(token, matterId) {
  return authFetch(`/matters/${matterId}`, { method: 'DELETE' }, token);
}

// ── Documents / Upload ────────────────────────────────────────────────────────

export async function generateUploadUrl(token, matterId) {
  return authFetch(`/matters/${matterId}/documents`, { method: 'POST' }, token);
}

// B9 (A7 backend pair): all runs of a matter, newest-first.
// Response: { matter_id, count, runs: [{ run_id, status, litfit_score,
//             created_at, updated_at, has_certificate }] }
export async function listRuns(token, matterId) {
  return authFetch(`/matters/${matterId}/runs`, {}, token);
}

// B9 (A6 backend pair): document records for a matter (metadata only -
// raw files are erased post-run; these rows are the practitioner-facing
// record). Response: { matter_id, count, documents: [{ original_filename,
// file_type, doc_category }] }  - note: no timestamp column exists.
export async function listDocuments(token, matterId) {
  return authFetch(`/matters/${matterId}/documents`, {}, token);
}

// Soft-delete / exclude a single document (run-doc-manage). Evidence docs are
// soft-excluded and the S3 file is KEPT (re-includable); the current brief is
// erased. Used by the readability-triage Exclude flow.
export async function deleteDocument(token, matterId, docId) {
  return authFetch(`/matters/${matterId}/documents/${docId}`, { method: 'DELETE' }, token);
}

export async function uploadToS3(presignedUrl, file) {
  // This is a direct S3 PUT. It does not use authFetch.
  const res = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': 'text/plain' },
  });
  if (!res.ok) throw new Error('S3 upload failed');
}

export async function confirmUpload(token, matterId, s3Key, fileName, acknowledgement = null) {
  return authFetch('/upload/confirm', {
    method: 'POST',
    body: JSON.stringify({
      matter_id: matterId,
      s3_key: s3Key,
      file_name: fileName,
      ...(acknowledgement ? { acknowledgement } : {}),
    }),
  }, token);
}

// ── Payment ───────────────────────────────────────────────────────────────────

export async function createCheckoutSession(token, matterId, perspective, runContext = {}, opts = {}) {
  return authFetch('/checkout/create', {
    method: 'POST',
    body: JSON.stringify({
      matter_id:   matterId,
      perspective,
      run_context: runContext,
      ...opts,
    }),
  }, token);
}

export async function confirmRun(token, runId, attestation = null) {
  // ATTEST-1: attestation = { text_version, attested, attested_at } from
  // RunConfirmModal. run-confirm Lambda persists it on the runs row.
  return authFetch('/run/confirm', {
    method: 'POST',
    body: JSON.stringify({
      run_id: runId,
      ...(attestation ? { attestation } : {}),
    }),
  }, token);
}

// ── Runs ──────────────────────────────────────────────────────────────────────

export async function getRunStatus(token, runId) {
  return authFetch(`/runs/${runId}/status`, {}, token);
}

export async function deleteRun(token, runId) {
  return authFetch(`/runs/${runId}`, { method: 'DELETE' }, token);
}

export async function submitFeedback(token, runId, rating, comment) {
  return authFetch(`/runs/${runId}/feedback`, {
    method: 'POST',
    body: JSON.stringify({ rating, comment }),
  }, token);
}